"use client";

import React, { useMemo } from "react";
import Image from "next/image";
import Link from "next/link";
import { ArrowRight } from "lucide-react";

interface ProductCategoryProps {
  data: {
    id: number;
    title?: string;
    description?: string;
    isLive: boolean;
    useH1Tag: boolean;
    Card: {
      id: number;
      title?: string;
      description?: string;
      href?: string;
      screenDisplay: boolean;
      tag?: string;
      image?: {
        data?: {
          attributes?: {
            url: string;
            alternativeText: string | null;
          };
        };
      };
    }[];
  };
}

const Layout1 = ({ data }: ProductCategoryProps) => {
  if (!data?.isLive) return null;

  const currentYear = useMemo(() => new Date().getFullYear(), []);

  const titleParts = data?.title?.split(" ") || [];
  const firstPart = titleParts[0] || "";
  const restPart = titleParts.slice(1).join(" ") || "";

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        {/* --- Section Header --- */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-8">
          <div className="space-y-4 max-w-3xl">
            <div className="flex items-center gap-3">
              <span
                className="w-8 h-[2px]"
                style={{ backgroundColor: "var(--secondary)" }}
              />
              <p className="text-[10px] font-black uppercase tracking-[0.4em] text-gray-700">
                Season {currentYear}
              </p>
            </div>

            <div className="text-4xl md:text-6xl font-black uppercase tracking-tighter text-neutral-900 leading-[0.9]">
              {data?.useH1Tag ? (
                <h1>
                  {firstPart}{" "}
                  <span
                    className="italic"
                    style={{ color: "var(--secondary)" }}
                  >
                    {restPart}
                  </span>
                </h1>
              ) : (
                <h2>
                  {firstPart}{" "}
                  <span
                    className="italic"
                    style={{ color: "var(--secondary)" }}
                  >
                    {restPart}
                  </span>
                </h2>
              )}
            </div>

            <p className="text-sm md:text-base text-gray-700 font-medium max-w-xl leading-relaxed tracking-tight">
              {data?.description}
            </p>
          </div>

          <Link
            href="/collections"
            className="group flex items-center gap-3 text-[11px] font-black uppercase tracking-[0.2em] text-neutral-900 border-b-2 border-neutral-900 pb-1 transition-colors hover:text-[var(--secondary)] hover:border-[var(--secondary)]"
          >
            Explore All
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        {/* --- Card Grid --- */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-x-5 gap-y-12">
          {data?.Card?.map(
            (card, index) =>
              card?.screenDisplay && (
                <div
                  key={card?.id || index}
                  className="group flex flex-col"
                >
                  <Link
                    href={card?.href || "#"}
                    className="relative aspect-[3/4] overflow-hidden bg-neutral-100 mb-5 block"
                  >
                    <Image
                      src={
                        card?.image?.data?.attributes?.url ||
                        "/placeholder.jpg"
                      }
                      alt={
                        card?.image?.data?.attributes?.alternativeText ||
                        card?.title ||
                        "Collection"
                      }
                      fill
                      sizes="(max-width: 640px) 50vw, (max-width: 1024px) 25vw, 16vw"
                      quality={80}
                      className="object-cover transition-transform duration-700 ease-out group-hover:scale-110"
                    />

                    {card?.tag && (
                      <div className="absolute top-0 left-0 z-10">
                        <span
                          className="inline-block px-3 py-1 text-[9px] font-black uppercase tracking-widest text-white"
                          style={{
                            backgroundColor:
                              card.tag.toLowerCase() === "sale"
                                ? "#dc2626"
                                : "var(--secondary)",
                          }}
                        >
                          {card.tag}
                        </span>
                      </div>
                    )}

                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/5 transition-colors duration-300" />
                  </Link>

                  <div className="space-y-1 text-center md:text-left">
                    <Link href={card?.href || "#"}>
                      <h3 className="text-[12px] font-black uppercase tracking-[0.1em] text-neutral-900 transition-colors hover:text-[var(--secondary)]">
                        {card?.title}
                      </h3>
                    </Link>

                    <div className="flex items-center justify-center md:justify-start gap-2">
                      <p className="text-[9px] font-bold uppercase tracking-[0.2em] text-gray-700 line-clamp-1">
                        {card?.description}
                      </p>

                      <div
                        className="h-[1.5px] w-0 group-hover:w-4 transition-all duration-300"
                        style={{ backgroundColor: "var(--secondary)" }}
                      />
                    </div>
                  </div>
                </div>
              )
          )}
        </div>
      </div>
    </section>
  );
};

export default Layout1;
